import { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Divider, Grid, Stack, Button, Typography, TextField, IconButton, FormControl, InputLabel as MuiInputLabel, Select, MenuItem } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import InputLabel from 'src/berry/ui-component/extended/Form/InputLabel.jsx';
import MainCard from 'src/berry/ui-component/cards/MainCard.jsx';
import { gridSpacing } from 'src/berry/store/constant.jsx';

import ModalsContext from 'src/context/modals.jsx';
import ErasContext from 'src/context/eras.jsx';

import PinsTable from './pins_table.jsx';
import FileSelector from 'src/components/file_selector/component.jsx';

const ContentItem = ({ control, index, content_items, setValue, watch, on_remove, style_key }) => {
	const item = content_items?.[index] || { type: 'text', data: '' };
	const item_type = watch(`car_content.${style_key}[${index}].type`) || item.type;
	const item_data = watch(`car_content.${style_key}[${index}].data`) || item.data;

	return (
		<>
			<Grid item xs={12}>
				<Stack direction="row" spacing={2} alignItems="flex-start">
					<Grid container spacing={gridSpacing} sx={{ flex: 1 }}>
						<Grid item xs={12} md={3}>
							<Stack spacing={gridSpacing}>
								<InputLabel>Type</InputLabel>
								<Controller
									name={`car_content.${style_key}[${index}].type`}
									control={control}
									defaultValue={item.type}
									render={({ field }) => (
										<FormControl fullWidth>
											<MuiInputLabel id={`car-content-type-${style_key}-${index}`}>Type</MuiInputLabel>
											<Select {...field} labelId={`car-content-type-${style_key}-${index}`} label="Type">
												<MenuItem value="img">Image</MenuItem>
												<MenuItem value="header">Header</MenuItem>
												<MenuItem value="text">Text</MenuItem>
												<MenuItem value="line_break">Line Break</MenuItem>
											</Select>
										</FormControl>
									)}
								/>
							</Stack>
						</Grid>

						<Grid item xs={12} md={9}>
							<Stack spacing={gridSpacing}>
								<InputLabel>Data</InputLabel>
								{item_type === 'img' ? (
									<FileSelector
										on_change={(data) => {
											setValue(`car_content.${style_key}[${index}].data`, data);
										}}
										value={item_data}
									/>
								) : item_type === 'line_break' ? (
									<></>
								) : (
									<Controller
										name={`car_content.${style_key}[${index}].data`}
										control={control}
										defaultValue={item.data || ''}
										render={({ field }) => <TextField {...field} fullWidth multiline={item_type === 'text' ? true : false} rows={item_type === 'text' ? 4 : 1} />}
									/>
								)}
							</Stack>
						</Grid>
					</Grid>
					<IconButton color="error" onClick={on_remove} sx={{ mt: 4 }}>
						<DeleteIcon />
					</IconButton>
				</Stack>
			</Grid>
			<Grid item xs={12}>
				<Divider />
			</Grid>
		</>
	);
};

const Era = () => {
	const { id } = useParams();
	const { list, set } = useContext(ErasContext);
	const modals_context = useContext(ModalsContext);

	const era = list[id];
	const pins = era?.pins || [];
	
	const car_content = era?.car_content || { '01': [], '02': [] };

	const { handleSubmit, control, setValue, watch, reset } = useForm({
		defaultValues: {
			car_content: {
				'01': [],
				'02': []
			}
		},
	});

	useEffect(() => {
		reset({
			car_content: {
				'01': car_content['01'] || [],
				'02': car_content['02'] || []
			}
		});
	}, [id, car_content]);

	const handle_pin_add = () => {
		modals_context.show('pin_add', {
			era_id: parseInt(id),
			clb: (data) => {
				if (!era.pins) era.pins = [];
				era.pins.push(data);
				const new_list = [...list];
				new_list[id] = era;
				set(new_list);
			},
		});
	};

	const handle_car_content_save = (form_data) => {
		era.car_content = form_data.car_content;
		const new_list = [...list];
		new_list[id] = era;
		set(new_list);
	};

	return (
		<MainCard title={`Edit Era: ${era?.name || ''}`}>
			<Grid container spacing={gridSpacing}>
				<Grid item xs={12}>
					<Stack spacing={gridSpacing}>
						<InputLabel>Era Name</InputLabel>
						<Typography variant="body1">{era?.name || ''}</Typography>
					</Stack>
				</Grid>

				<Grid item xs={12}>
					<Stack spacing={gridSpacing}>
						<InputLabel>Stats</InputLabel>
						<Grid container spacing={1}>
							<Grid item xs={12/5}>
								<Typography variant="body2">Year:<br />{era?.stats?.year || ''}</Typography>
							</Grid>
							<Grid item xs={12/5}>
								<Typography variant="body2">Population:<br />{era?.stats?.population || ''}</Typography>
							</Grid>
							<Grid item xs={12/5}>
								<Typography variant="body2">Members:<br />{era?.stats?.members || ''}</Typography>
							</Grid>
							<Grid item xs={12/5}>
								<Typography variant="body2">Highway:<br />{era?.stats?.highway || ''}</Typography>
							</Grid>
							<Grid item xs={12/5}>
								<Typography variant="body2">Covered:<br />{era?.stats?.covered || ''}</Typography>
							</Grid>
						</Grid>
					</Stack>
				</Grid>

				<Grid item xs={12}>
					<Divider />
				</Grid>

				<Grid item xs={12}>
					<InputLabel>Pins</InputLabel>
				</Grid>

				<PinsTable
					fields={pins}
					remove={(index) => {
						era.pins.splice(index, 1);
						const new_list = [...list];
						new_list[id] = era;
						set(new_list);
					}}
					update={(index, data) => {
						era.pins[index] = data;
						const new_list = [...list];
						new_list[id] = era;
						set(new_list);
					}}
					era_id={parseInt(id)}
				/>

				<Grid item xs={12}>
					<Button variant="text" onClick={handle_pin_add}>
						Add Pin
					</Button>
				</Grid>

				<Grid item xs={12}>
					<Divider />
				</Grid>

				<Grid item xs={12}>
					<InputLabel>Vehicle Content</InputLabel>
				</Grid>

				<Grid item xs={12}>
					<form onSubmit={handleSubmit(handle_car_content_save)}>
						<Grid container spacing={gridSpacing}>
							<Grid item xs={12} md={6}>
								<Stack spacing={gridSpacing}>
									<InputLabel>Style 01 Content</InputLabel>
									{(watch('car_content.01') || []).map((_, index) => (
										<ContentItem
											key={index}
											control={control}
											index={index}
											content_items={watch('car_content.01')}
											setValue={setValue}
											watch={watch}
											style_key="01"
											on_remove={() => {
												const current_content = watch('car_content.01') || [];
												const new_content = current_content.filter((_, i) => i !== index);
												setValue('car_content.01', new_content);
											}}
										/>
									))}
									<Button
										variant="outlined"
										fullWidth
										startIcon={<AddIcon />}
										onClick={() => {
											const current_content = watch('car_content.01') || [];
											const new_item = { type: 'text', data: '' };
											setValue('car_content.01', [...current_content, new_item]);
										}}
									>
										Add Item
									</Button>
								</Stack>
							</Grid>

							<Grid item xs={12} md={6}>
								<Stack spacing={gridSpacing}>
									<InputLabel>Style 02 Content</InputLabel>
									{(watch('car_content.02') || []).map((_, index) => (
										<ContentItem
											key={index}
											control={control}
											index={index}
											content_items={watch('car_content.02')}
											setValue={setValue}
											watch={watch}
											style_key="02"
											on_remove={() => {
												const current_content = watch('car_content.02') || [];
												const new_content = current_content.filter((_, i) => i !== index);
												setValue('car_content.02', new_content);
											}}
										/>
									))}
									<Button
										variant="outlined"
										fullWidth
										startIcon={<AddIcon />}
										onClick={() => {
											const current_content = watch('car_content.02') || [];
											const new_item = { type: 'text', data: '' };
											setValue('car_content.02', [...current_content, new_item]);
										}}
									>
										Add Item
									</Button>
								</Stack>
							</Grid>

							<Grid item xs={12}>
								<Button variant="contained" type="submit">
									Save Vehicle Content
								</Button>
							</Grid>
						</Grid>
					</form>
				</Grid>
			</Grid>
		</MainCard>
	);
};

export default Era;

